import app from 'flarum/forum/app';

/**
 * 微信相关前端公共工具。
 *
 * 两个 scope 的区别（微信官方）：
 *  - snsapi_base    静默授权：用户无感知，只拿 openid，拿不到昵称头像
 *  - snsapi_userinfo 显式授权：会弹「确认登录」页，能拿昵称/头像/地区/unionid
 * 两者都拿不到手机号 —— 微信从未通过 H5 网页授权开放手机号。
 */

export function isInWechat() {
  return typeof navigator !== 'undefined' && /micromessenger/i.test(navigator.userAgent || '');
}

/**
 * 构造微信授权跳转地址。
 *
 * @param {'mp'|'open'} mode
 * @param {string} appId
 * @param {'snsapi_base'|'snsapi_userinfo'|'snsapi_login'} scope
 * @param {string} returnTo 授权完成后要回到的站内路径（会原样放进 state，
 *                          后端只接受相对路径，防开放重定向）
 */
export function buildAuthUrl(mode, appId, scope, returnTo) {
  const forumUrl = (app.forum && app.forum.attribute('baseUrl')) || '';
  const redirectUri = encodeURIComponent(forumUrl + '/auth/wechat');

  const base =
    mode === 'mp'
      ? 'https://open.weixin.qq.com/connect/oauth2/authorize'
      : 'https://open.weixin.qq.com/connect/qrconnect';

  return (
    base +
    '?appid=' +
    encodeURIComponent(appId) +
    '&redirect_uri=' +
    redirectUri +
    '&response_type=code' +
    '&scope=' +
    encodeURIComponent(scope) +
    '&state=' +
    encodeURIComponent(returnTo || '/') +
    '#wechat_redirect'
  );
}

/**
 * 微信内置浏览器里的静默自动登录。
 *
 * 三个必须守住的点：
 * 1. 每个标签页只自动跳一次（sessionStorage 标记）—— 否则 code 失效/建号失败时会
 *    在「论坛 → 微信授权 → 论坛」之间无限往返，用户卡死在闪屏。
 * 2. 已登录、非微信浏览器、非公众号模式，一律不触发。
 * 3. 已绑定用户才自动登录；新用户不自动建号（见后端 WechatLoginController 注释）。
 */
const TRIED_KEY = 'acme_wechat_autologin_tried';

export function tryWechatAutoLogin() {
  const cfg = (app.data && app.data.acmeWechat) || {};

  if (!cfg.autoLogin) return;
  if (cfg.mode !== 'mp' || !cfg.mpAppId) return;
  if (app.session && app.session.user) return;
  if (!isInWechat()) return;
  if (sessionStorage.getItem(TRIED_KEY)) return;

  // 正在回调中（微信带 code 跳回来）就不要再发起一次
  if (/[?&]code=/.test(window.location.search)) return;
  if (window.location.pathname.indexOf('/auth/wechat') === 0) return;

  sessionStorage.setItem(TRIED_KEY, '1');

  window.location.href = buildAuthUrl(
    'mp',
    cfg.mpAppId,
    'snsapi_base',
    window.location.pathname + window.location.search
  );
}
